import { useMemo, useState } from 'react';
import { PayoutFilters } from '../models';

interface UsePayoutsMainProps {
    initialFilters?: PayoutFilters;
}

// Mapeo de campos de AG-Grid a campos de API
// Solo total_amount y payout_date son ordenables según los requisitos
const FIELD_TO_API_SORT: Record<string, string> = {
    'total_amount': 'total_amount',
    'payout_date': 'payout_date',
};

export const usePayoutsMain = ({
    initialFilters = {},
}: UsePayoutsMainProps = {}) => {
    const [filters, setFilters] = useState<PayoutFilters>({
        page: 1,
        per_page: 15,
        sort_by: 'payout_date',
        sort_direction: 'desc',
        ...initialFilters,
    });

    const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>({
        field: 'payout_date',
        direction: 'desc',
    });

    // Convertir sorting de AG-Grid a filtros de API
    const apiFilters = useMemo(() => {
        const apiField = sorting?.field
            ? FIELD_TO_API_SORT[sorting.field] || sorting.field
            : undefined;
        const apiDirection = sorting?.direction;

        // Construir filtros base sin sort_by y sort_direction
        const { sort_by: _, sort_direction: __, ...baseFilters } = filters;

        return {
            ...baseFilters,
            ...(apiField ? { sort_by: apiField } : {}),
            ...(apiDirection ? { sort_direction: apiDirection } : {}),
        };
    }, [filters, sorting]);

    const updateFilters = (newFilters: Partial<PayoutFilters>) => {
        setFilters((prev) => ({
            ...prev,
            ...newFilters,
            // Reset page when filters change (except when changing page)
            page: newFilters.page !== undefined ? newFilters.page : 1,
        }));
    };

    const resetFilters = () => {
        setFilters({
            page: 1,
            per_page: 15,
            sort_by: 'payout_date',
            sort_direction: 'desc',
        });
        setSorting({
            field: 'payout_date',
            direction: 'desc',
        });
    };

    const changePage = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const changePageSize = (per_page: number) => {
        setFilters((prev) => ({ ...prev, per_page, page: 1 }));
    };

    const changeSorting = (sort_by: string, sort_direction: 'asc' | 'desc') => {
        setFilters((prev) => ({ ...prev, sort_by, sort_direction, page: 1 }));
    };

    const handleSortingChange = (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
        setSorting(newSorting);
        // Limpiar sort_by y sort_direction de los filtros cuando se limpia el sorting
        setFilters((prev) => {
            const { sort_by: _, sort_direction: __, ...rest } = prev;
            return { ...rest, page: 1 };
        });
    };

    return {
        filters: apiFilters, // Usar apiFilters que incluye el sorting correcto
        updateFilters,
        resetFilters,
        changePage,
        changePageSize,
        changeSorting,
        handleSortingChange,
        sorting,
    };
};
